import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../../services/common.service';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
    selector: 'app-text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['../../styles.scss', './text-input.component.scss'],
    standalone: false
})
export class TextInputComponent implements OnChanges, OnDestroy {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label = '';
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() focus = false;
  @Input() searchComponent = false;
  @Input() placeholder = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName = '';
  @Input() error = '';
  @Input() warning = '';
  @Input() hint = '';
  @Input() maxLength: string | number = 50;
  @Input() max = 9999999999;
  @Input() min = 0;
  @Input() prefixIcon = '';
  @Input() suffixIcon = '';
  @Input() suffixText = '';
  @Input() type: 'text' | 'password' | 'url' = 'text';
  @Output() checkForErrors = new EventEmitter();
  @Output() fieldBlur = new EventEmitter();

  fieldSubscription: Subscription | undefined;
  matcher = new CustomErrorStateMatcher();

  isRequired = false;
  isDisabled = false;

  // The cap actually put on the <input>. A stored value can already be longer than
  // maxLength (mobile and the API do not cap these fields), and enforcing the cap on
  // such a value only freezes the field — the browser refuses every insertion while
  // the text stays too long — so we drop the cap for that field. Normal-length values
  // keep it. Mirrors TextAreaComponent.
  effectiveMaxLength: string | number = null;

  constructor(private commonService: CommonService) {}

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event: Event) {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
    if (this.searchComponent) {
      this.commonService.focusedIn.next(false);
    }
    this.fieldBlur.emit();
  }

  @HostListener('focusin')
  setInputFocus(): void {
    if (this.searchComponent) {
      this.commonService.focusedIn.next(true);
    }
  }

  ngOnChanges(): void {
    const control = this.parentFormGroup?.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';
    const currentLength = (control?.value ?? '').toString().length;
    this.effectiveMaxLength = currentLength > Number(this.maxLength) ? null : this.maxLength;
    this.fieldSubscription = control?.valueChanges.subscribe(value => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName]?.touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;

      // Clear existence, cross-field, and validation errors when user types
      if (errors && (errors['emailExists'] || errors['phoneExists'] || errors['emailOrPhoneRequired'] || errors['invalidPhone'])) {
        const updatedErrors = { ...errors };
        delete updatedErrors['emailExists'];
        delete updatedErrors['phoneExists'];
        delete updatedErrors['emailOrPhoneRequired'];
        delete updatedErrors['invalidPhone'];
        this.parentFormGroup.controls[this.controlName].setErrors(
          Object.keys(updatedErrors).length > 0 ? updatedErrors : null
        );
        this.checkForErrors.emit(this.controlName);
      } else if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}

class CustomErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}
